<template>
    <section class="section">
        <div class="level is-mobile">
            <div class="level-left">
                <div class="level-item">
                    <h1 class="title">Legs</h1>
                </div>
            </div>
            <!-- Error Message -->
            <div v-if="errorMessage" class="notification is-danger">
                {{ errorMessage }}
            </div>
            <div class="level-right">
                <div class="level-item">
                    <span class="mr-2">Filter</span>
                    <div class="select">
                        <select v-model="selectedFilter">
                            <option value="all">All</option>
                            <option value="review">Needs Review</option>
                            <option value="warning">Warning</option>
                            <option value="failed">Failed</option>
                            <option value="success">Passed</option>
                        </select>
                    </div>
                </div>
                <div class="level-item">
                    <button :class="buttonClass" @click="handleRefresh">
                        <template v-if="syncStore.syncData.status === 'running'">
                            Running Sync...
                        </template>
                        <template v-else-if="syncStore.syncData.status === 'error'">
                            Error in Sync
                        </template>
                        <template v-else>
                            Last Refresh {{ timeSinceLastSync }} ago&nbsp;
                            <font-awesome-icon :icon="['fas', 'rotate-right']" />
                        </template>
                    </button>
                </div>
            </div>
        </div>
        <legs-table :legs="legs" :filter="selectedFilter"></legs-table>
    </section>
</template>

<script>
import LegsTable from './LegsTable';
import { useLegsStore } from '@/store/legsStore';
import { useSyncStore } from '@/store/syncStore';
import { ref, computed, onMounted, onUnmounted } from 'vue';

export default {
    setup() {
        const legsStore = useLegsStore();
        const syncStore = useSyncStore();
        const selectedFilter = ref('all');
        const errorMessage = ref(null);
        const isSyncing = ref(false);

        legsStore.fetchLegs();
        syncStore.fetchLatestSync();

        // Reactive reference to hold the current time
        const currentTime = ref(new Date());

        // Computed property to calculate time since last sync
        const timeSinceLastSync = computed(() => {
            const syncData = syncStore.syncData;
            if (!syncData.datetime) return 'never';
            const lastSync = new Date(syncData.datetime);
            const diffInMinutes = Math.max(0, Math.floor((currentTime.value - lastSync) / 60000));
            return `${diffInMinutes} min`;
        });

        const buttonClass = computed(() => {
            const syncData = syncStore.syncData;
            if (syncData.status === 'running') return 'button is-warning';
            if (syncData.status === 'completed') return 'button is-info';
            if (syncData.status === 'error') return 'button is-danger';
            return 'button';
        });

        let updateInterval = null;

        onMounted(() => {
            // Update currentTime every minute to trigger re-computation
            updateInterval = setInterval(() => {
                currentTime.value = new Date();
            }, 60000); // Update every minute
        });

        onUnmounted(() => {
            clearInterval(updateInterval);
        });

        const handleRefresh = async () => {
            if (isSyncing.value) return;

            errorMessage.value = null; // Clear previous errors
            syncStore.syncData.status = 'running';
            isSyncing.value = true;
            const response = await syncStore.runSyncLegs();
            isSyncing.value = false;

            if (!response.success) {
                errorMessage.value = response.message;
            }
            else {
                errorMessage.value = null;
            }
        };

        return {
            legs: legsStore.legs,
            selectedFilter,
            timeSinceLastSync,
            buttonClass,
            syncStore,
            errorMessage,
            handleRefresh,
            isSyncing
        }
    },
    components: {
        LegsTable
    }
}
</script>